/**
 * VIEW AIRPLANES' 3D view: one aeroplane, parked on the aerodrome the original
 * parks it on, under the sky the original draws.
 *
 * It is a sibling of `career/aircraft-preview.ts` rather than an option on it,
 * and the split is real rather than tidiness. PERSONAL AIRCRAFT composites a
 * *transparent* turntable over `G-AERO.SCR`'s painted aerodrome — its scene has
 * no ground, no sky and no horizon because the picture behind it supplies all
 * three. This screen's original has no painted backdrop at all
 * (`shell/plane-viewer-surface.ts`); it renders a scene. Two different jobs, and
 * everything they do share — `flight/aircraft-mesh.ts`'s loader, the pose
 * ladder, ENHANCED MODELS — is imported rather than copied.
 *
 * ## The camera is level, and stands on the field
 *
 * `aerodrome-stage.ts` has the derivation of where it stands. What it *does* is
 * simpler than the career turntable: no pitch. The camera orbits at
 * `PARKED_EYE_M` and looks at that same height, so the skyline lands across the
 * middle of the frame — which is where `docs/game/ui-reference/view-airplanes-3d.png`
 * puts it (logical y 78 of a viewport running 5 to 139, i.e. 54 % down) — and
 * the aeroplane, whose centre is below a standing man's eye, sits in the lower
 * half in front of it. The career preview's `PITCH_RAD` of -0.32 would put the
 * horizon 18 degrees above a 30-degree frame, i.e. off the top, and that is
 * exactly why a turntable in a void could carry it and this cannot.
 *
 * ## It animates
 *
 * The career preview renders on demand, because nothing in it moves. This one
 * runs a `requestAnimationFrame` loop, because the propeller turns.
 *
 * ## Depth
 *
 * `reversedDepthBuffer`, near 0.5 m, far 26 km — the practice range's own hills
 * stand 10 to 18 km out while the aeroplane is 12 m away, and `flight-view.ts`
 * records what a conventional buffer does with that span. Nothing here sets
 * `depthWrite: false`.
 */

import * as THREE from "three/webgpu";
import type { Pose } from "@red-opus/engine";
import { disposeTree } from "../three/dispose-tree.ts";
import { disableWebGlFallback } from "../three/webgpu-support.ts";
import { loadAircraftMesh, shadingPalette } from "../flight/aircraft-mesh.ts";
import { CAMERA_FAR_M } from "../flight/flight-view.ts";
import { createPropellerDiscs, type PropellerDiscs, type PropellerSource } from "../flight/propeller-discs.ts";
import { modelledPropellerVisible } from "../flight/modelled-propeller.ts";
import { setModelledPropellerVisible } from "../three/enhanced-nodes.ts";
import { DEFAULT_EFFECTS_SETTINGS, type EffectsSettings } from "../flight/effects-settings.ts";
import { applyViewPixelRatio, viewPixelRatio } from "../three/view-pixel-ratio.ts";
import { clampZoom, zoomDistance, type PreviewPose } from "../career/preview-pose.ts";
import { createAerodromeStage, type AerodromeStage } from "./aerodrome-stage.ts";
import { createGroundShadow, groundContactLift } from "./parked-aircraft.ts";
import { PARKED_EYE_M, PARKED_HEADING_DEG, PARKED_RPM_FRACTION, PARKED_YAW_OFFSET_DEG } from "./parked-look.ts";

/** Matches `career/aircraft-preview.ts`, and `aerodrome-stage.ts`'s own fit of the original's capture endorses it. */
const FOV_DEG = 30;
/** Well inside the closest zoom step; the far plane is the flight camera's, because the hills are the flight world's. */
const NEAR_M = 0.5;

export interface ParkedPreview {
  /** Loads (or reloads) the aeroplane. Safe to call every frame; an unchanged request is ignored. */
  show(modelId: string): void;
  setPose(pose: PreviewPose): void;
  /** PROPELLER EFFECT / ENHANCED MODELS / ENHANCED EFFECTS, from the shared store. */
  setEffects(settings: EffectsSettings): void;
  resize(width: number, height: number): void;
  dispose(): void;
}

interface ParkedState {
  readonly renderer: THREE.WebGPURenderer;
  readonly scene: THREE.Scene;
  readonly camera: THREE.PerspectiveCamera;
  /** Holds the aeroplane, lifted onto its wheels. */
  readonly pivot: THREE.Group;
  stage: AerodromeStage | undefined;
  discs: PropellerDiscs | undefined;
  shadow: THREE.Mesh | undefined;
  effects: EffectsSettings | undefined;
  modelId: string | null;
  /** Metres the aeroplane was raised by, so the propeller pose matches it. */
  lift: number;
  radius: number;
  pose: PreviewPose;
  /** What `show` was last asked for, so an unchanged request does not refetch. */
  key: string;
  generation: number;
  frame: number;
  lastMs: number;
  disposed: boolean;
}

function place(state: ParkedState): void {
  const distance = zoomDistance(state.pose, state.radius);
  const yaw = ((state.pose.yawDeg + PARKED_YAW_OFFSET_DEG) * Math.PI) / 180;
  state.camera.position.set(Math.sin(yaw) * distance, PARKED_EYE_M, Math.cos(yaw) * distance);
  state.camera.lookAt(0, PARKED_EYE_M, 0);
}

const PARKED_HEADING_RAD = (PARKED_HEADING_DEG * Math.PI) / 180;

/**
 * The parked aeroplane's contribution to the propeller layer — one source,
 * standing still, carrying the same heading the pivot does so the disc stays on
 * the nose rather than beside it.
 */
function propellerSources(state: ParkedState): PropellerSource[] {
  if (!state.modelId) return [];
  const half = PARKED_HEADING_RAD / 2;
  const pose: Pose = {
    position: { x: 0, y: state.lift, z: 0 },
    orientation: { x: 0, y: Math.sin(half), z: 0, w: Math.cos(half) },
  };
  return [{ id: "parked", modelId: state.modelId, pose, rpmFraction: PARKED_RPM_FRACTION }];
}

function renderFrame(state: ParkedState, nowMs: number): void {
  if (state.disposed) return;
  const dtS = state.lastMs === 0 ? 0 : Math.min(0.1, (nowMs - state.lastMs) / 1000);
  state.lastMs = nowMs;
  state.discs?.update(dtS, propellerSources(state), false);
  void state.renderer.renderAsync(state.scene, state.camera);
}

/**
 * Redraws the ground shadow from whatever the aeroplane currently *shows*.
 *
 * Called again when PROPELLER EFFECT is flipped, because the modelled propeller
 * goes with it and the projection walks the drawn triangles
 * (`parked-aircraft.ts`). The lift and the zoom radius do not need the same
 * treatment: both come off a `Box3`, which ignores visibility, so the aeroplane
 * neither rises nor jumps when the blade is hidden.
 */
function refreshShadow(state: ParkedState): void {
  if (state.shadow) {
    disposeTree(state.shadow);
    state.scene.remove(state.shadow);
  }
  state.shadow = createGroundShadow(state.pivot);
  if (state.shadow) state.scene.add(state.shadow);
}

/** Swaps in a newly loaded aeroplane: heading, ground contact, shadow, zoom radius. */
function adopt(state: ParkedState, modelId: string, mesh: THREE.Object3D): void {
  disposeTree(state.pivot);
  state.pivot.clear();
  state.pivot.add(mesh);
  state.pivot.rotation.y = PARKED_HEADING_RAD;
  state.pivot.position.y = 0;
  // Through the pivot, so the heading is already in the box being measured.
  state.lift = groundContactLift(state.pivot);
  state.pivot.position.y = state.lift;
  state.modelId = modelId;

  refreshShadow(state);

  const box = new THREE.Box3().setFromObject(mesh);
  state.radius = Math.max(0.5, box.getSize(new THREE.Vector3()).length() / 2);
  place(state);
}

/**
 * The WebGPU canvas, sized by CSS and filling `container`; `undefined` where
 * WebGPU is missing.
 *
 * The buffer starts on the ENHANCED MODELS **default** rather than on the
 * player's stored choice, because `createParkedPreview` runs before the first
 * `setEffects` — `applyEffects` corrects it a moment later and on every flip
 * after that, which is the same shape the propeller discs and the geometry key
 * in this file already have.
 */
function createRenderer(container: HTMLElement): THREE.WebGPURenderer | undefined {
  let renderer: THREE.WebGPURenderer;
  try {
    renderer = new THREE.WebGPURenderer({ antialias: true, reversedDepthBuffer: true });
  } catch {
    return undefined;
  }
  disableWebGlFallback(renderer);
  renderer.setPixelRatio(viewPixelRatio(DEFAULT_EFFECTS_SETTINGS.enhancedModels));
  container.appendChild(renderer.domElement);
  renderer.domElement.style.width = "100%";
  renderer.domElement.style.height = "100%";
  renderer.domElement.style.display = "block";
  return renderer;
}

/**
 * Starts the aerodrome and the propeller layer.
 *
 * Both are independent of which aeroplane is on screen, so neither waits for
 * the first `show`: the scene belongs to the screen, and the aerodrome should
 * be up before its aeroplane arrives.
 */
function attachSceneParts(state: ParkedState): void {
  void createAerodromeStage().then((stage) => {
    if (state.disposed) {
      stage.dispose();
      return;
    }
    state.stage = stage;
    state.scene.add(stage.group);
  });
  void shadingPalette().then((palette) => {
    if (state.disposed || !palette) return;
    const discs = createPropellerDiscs(palette);
    state.discs = discs;
    discs.setEnabled(state.effects?.propellerEffect ?? true);
    state.scene.add(discs.group);
  });
}

/**
 * A new toggle-row state, applied to the screen. Two of the three settings reach
 * the picture here, and they reach it by different routes.
 *
 * **PROPELLER EFFECT is one switch over two propellers.** The disc layer is
 * turned on or off, and the enhanced `.glb`'s own modelled blades do the
 * opposite (`flight/modelled-propeller.ts`) — exactly one of them is ever on the
 * nose. Neither half reloads anything: the layer is a flag and the blades are a
 * node's visibility. The ground shadow *is* retaken, because it is projected
 * from the triangles that are actually drawn (`parked-aircraft.ts`).
 *
 * **ENHANCED MODELS changes which geometry exists**, so it cannot be a flag: the
 * aeroplane has to be reloaded. Clearing `key` is what makes the next `show`
 * call a miss rather than a no-op.
 */
function applyEffects(state: ParkedState, settings: EffectsSettings): void {
  const previous = state.effects;
  state.effects = settings;
  state.discs?.setEnabled(settings.propellerEffect);
  if (state.modelId && previous?.propellerEffect !== settings.propellerEffect) {
    setModelledPropellerVisible(state.pivot, modelledPropellerVisible(state.modelId, settings.propellerEffect));
    refreshShadow(state);
  }
  if (previous && previous.enhancedModels !== settings.enhancedModels) state.key = "";
  // ENHANCED MODELS also lifts the resolution cap (`three/view-pixel-ratio.ts`),
  // and this screen follows `/fly` rather than keeping a policy of its own —
  // it is the screen a player goes to *to look at the aeroplane*. three's
  // `setPixelRatio` resizes the buffer itself and nothing on this stage is
  // measured in drawing-buffer pixels, so there is no second step here.
  applyViewPixelRatio(state.renderer, settings.enhancedModels);
}

/**
 * Attaches a preview to `container`, or returns `undefined` where WebGPU is not
 * available — the same degradation `/fly` and the career turntable make: the
 * 2D screen stays, the 3D view simply is not there.
 */
export function createParkedPreview(container: HTMLElement): ParkedPreview | undefined {
  const renderer = createRenderer(container);
  if (!renderer) return undefined;

  const state: ParkedState = {
    renderer,
    scene: new THREE.Scene(),
    camera: new THREE.PerspectiveCamera(FOV_DEG, 1, NEAR_M, CAMERA_FAR_M),
    pivot: new THREE.Group(),
    stage: undefined,
    discs: undefined,
    shadow: undefined,
    effects: undefined,
    modelId: null,
    lift: 0,
    radius: 6,
    pose: { yawDeg: 0, zoom: 0 },
    key: "",
    generation: 0,
    frame: 0,
    lastMs: 0,
    disposed: false,
  };
  state.scene.add(state.pivot);
  place(state);
  attachSceneParts(state);

  const tick = (nowMs: number): void => {
    if (state.disposed) return;
    state.frame = requestAnimationFrame(tick);
    renderFrame(state, nowMs);
  };
  state.frame = requestAnimationFrame(tick);

  return {
    show(modelId) {
      const enhanced = state.effects?.enhancedModels ?? true;
      const key = `${modelId}|${enhanced ? "enhanced" : "baked"}`;
      if (key === state.key) return;
      state.key = key;
      const generation = ++state.generation;
      // This screen owns a `PropellerDiscs` of its own and AD-012 §"PS.EXE" is
      // why: the original's VIEW AIRPLANES really does draw a parked propeller,
      // disc and blades at once (`parked-look.ts`'s `PARKED_RPM_FRACTION`). So
      // when the effect is on it is the propeller here too, and the enhanced
      // `.glb`'s own blades stand down; when it is off they are what is left,
      // which is nearer the original's picture than a bare hub.
      void loadAircraftMesh(modelId, { enhanced, propellerEffect: state.effects?.propellerEffect ?? true }).then((mesh) => {
        if (state.disposed || generation !== state.generation || !mesh) return;
        adopt(state, modelId, mesh);
      });
    },
    setPose(pose) {
      state.pose = { yawDeg: pose.yawDeg, zoom: clampZoom(pose.zoom) };
      place(state);
    },
    setEffects: (settings) => applyEffects(state, settings),
    resize(width, height) {
      if (width < 1 || height < 1) return;
      state.renderer.setSize(width, height, false);
      state.camera.aspect = width / height;
      state.camera.updateProjectionMatrix();
    },
    dispose() {
      state.disposed = true;
      cancelAnimationFrame(state.frame);
      state.discs?.dispose();
      state.stage?.dispose();
      disposeTree(state.scene);
      state.renderer.domElement.remove();
      state.renderer.dispose();
    },
  };
}
